import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { DecorationSet, EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../aiPluginType';

import { checkContentEligibility } from './content-eligibility';
import { hasTabConflict } from './context-conflict-detector';
import { createNudgeDecorationSet } from './decorations';
import { IdleDetector } from './idle-detector';
import {
	createPluginState,
	createShowPromptCommand,
	hidePromptCommand,
	initialState,
	pluginKey,
} from './plugin-factory';
import { getStandDownManagerOptions, StandDownManager } from './stand-down-manager';

/**
 * Create the ai-quick-prompt ProseMirror plugin.
 *
 * @param options - The options
 * @param options.api - The editor plugin API
 * @param options.dispatch - The plugin dispatch function from pluginFactory
 * @param options.getIntl - Function to get intl for i18n
 * @returns A ProseMirror Plugin instance
 */
export function createAiQuickPromptPlugin({
	dispatch,
	api,
	getIntl,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	dispatch: Dispatch;
	getIntl: PMPluginFactoryParams['getIntl'];
}): SafePlugin {
	return new SafePlugin({
		key: pluginKey,
		state: createPluginState(dispatch, initialState),

		view(view: EditorView) {
			const standDownManager = new StandDownManager(getStandDownManagerOptions({ api }));

			const idleDetector = new IdleDetector({
				onIdle: () => {
					// - Check stand-down
					if (standDownManager.shouldStandDown()) {
						return;
					}

					// - Check content eligibility
					const eligibleRange = checkContentEligibility({
						locale: getIntl().locale,
						selection: view.state.selection,
					});
					if (!eligibleRange) {
						return;
					}

					// - Check Tab conflict
					if (hasTabConflict(view.state.selection)) {
						return;
					}

					// - Show the prompt
					createShowPromptCommand(eligibleRange)(view.state, view.dispatch);
				},
			});

			return {
				update(view: EditorView, prevState: EditorState) {
					// When document changes, hide the prompt and reset idle timer
					// The plugin factory's onDocChanged callback will be called whenever there is a step,
					// which is not always a document change.
					// Selection changes are handled by pluginFactory's onSelectionChanged callback
					if (!prevState.doc.eq(view.state.doc)) {
						const pluginState = pluginKey.getState(view.state);
						if (pluginState?.isPromptVisible) {
							// Hide the prompt after a short delay to allow the editor to complete the current state update.
							// See https://hello.jira.atlassian.cloud/browse/EDITOR-3849
							setTimeout(() => {
								hidePromptCommand(view.state, view.dispatch);
							}, 0);
						}
						idleDetector.reset();
					}
				},
				destroy() {
					idleDetector.destroy();
				},
			};
		},

		props: {
			decorations(state: EditorState): DecorationSet | undefined {
				const pluginState = pluginKey.getState(state);
				if (pluginState?.isPromptVisible && pluginState.eligibleNodeRange) {
					return createNudgeDecorationSet({
						state,
						range: pluginState.eligibleNodeRange,
						getIntl,
					});
				}
				return undefined;
			},
		},
	});
}
